import React, { useEffect, useState } from 'react';
import { Button, TextField } from '@mui/material';
import { FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as XlsxIcon } from '@mui/icons-material';
import { downloadCsv } from '../../utils/csv';
import { incomeStatementCsv } from '../../utils/financialReportCsv';
import SearchableSelect from '../common/SearchableSelect';
import {
    Account,
    SegmentIncomeStatementResponse,
    SegmentIncomeStatementBlock,
    Segment,
} from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate, firstOfYearLocalIso, formatDate } from '../../utils/dateUtils';
import {
    CompareMode,
    Period,
    previousPeriod,
    sameLastYear,
    wantsLastYear,
    wantsPrevious,
} from '../../utils/reportPeriods';
import {
    ExpandControls,
    ExpandState,
    FigureHeaders,
    SectionLabelRow,
    StatementTreeRows,
    TotalRow,
    collapseAll,
    expandAll,
    initialExpandState,
    ledgerLink,
    statementHeaderStyle,
    toggleKey,
} from './StatementTree';

const today = new Date();
const firstOfYear = firstOfYearLocalIso(today);
const todayStr = toLocalIsoDate(today);

/** What the report on screen was fetched with: exports and comparatives follow this, not the inputs. */
interface Applied {
    from: string;
    to: string;
    segmentId?: string;
    compare: CompareMode;
    previous?: Period;
    lastYear?: Period;
}

const SegmentIncomeStatement: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<SegmentIncomeStatementResponse | null>(null);
    const [previousReport, setPreviousReport] = useState<SegmentIncomeStatementResponse | null>(null);
    const [lastYearReport, setLastYearReport] = useState<SegmentIncomeStatementResponse | null>(null);
    const [applied, setApplied] = useState<Applied | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [expand, setExpand] = useState<ExpandState>(initialExpandState);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [from, setFrom] = useState(firstOfYear);
    const [to, setTo] = useState(todayStr);
    const [compare, setCompare] = useState<CompareMode>('NONE');
    const [segments, setSegments] = useState<Segment[]>([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');

    useEffect(() => {
        const loadSegments = async () => {
            try {
                const page = await SegmentService.getByInstitution();
                setSegments(page.content ?? []);
            } catch (err) {
                toast.showApiError(err, 'Failed to load segments');
            }
        };
        const loadAccounts = async () => {
            try {
                setAccounts((await AccountService.getAllAccounts(true)) ?? []);
            } catch (err) {
                // The tree is a nicety: without the chart the statement still shows its flat lines.
                setAccounts([]);
            }
        };
        loadSegments();
        loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const segmentId = selectedSegmentId || undefined;
            const prev = wantsPrevious(compare) ? previousPeriod(from, to) : undefined;
            const ly = wantsLastYear(compare) ? sameLastYear(from, to) : undefined;
            const [data, prevData, lyData] = await Promise.all([
                AccountService.getIncomeStatement(from, to, segmentId),
                prev ? AccountService.getIncomeStatement(prev.from, prev.to, segmentId) : Promise.resolve(null),
                ly ? AccountService.getIncomeStatement(ly.from, ly.to, segmentId) : Promise.resolve(null),
            ]);
            setReport(data);
            setPreviousReport(prevData);
            setLastYearReport(lyData);
            setApplied({ from, to, segmentId, compare, previous: prev, lastYear: ly });
            setExpand(initialExpandState);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segment income statement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasData = report && report.segments.length > 0;

    // Exports the report on screen (its own dates), never the unapplied inputs.
    const handleExport = () => {
        if (!report) return;
        const { headers, rows } = incomeStatementCsv(report);
        downloadCsv(`income-statement-${report.fromDate}-to-${report.toDate}.csv`, headers, rows);
    };

    const handleDownload = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try {
            setDownloading(true);
            await AccountService.downloadIncomeStatement(applied.from, applied.to, applied.segmentId, applied.compare, format);
        } catch (err) {
            toast.showApiError(err, 'Failed to download the income statement');
        } finally {
            setDownloading(false);
        }
    };

    const netProfitColor = (value: number) => (value >= 0 ? '#2e7d32' : '#c62828');

    const comparatives: { label: string; report: SegmentIncomeStatementResponse | null }[] = [];
    if (applied && wantsPrevious(applied.compare)) comparatives.push({ label: 'Previous period', report: previousReport });
    if (applied && wantsLastYear(applied.compare)) comparatives.push({ label: 'Same period last year', report: lastYearReport });
    const comparativeLabels = comparatives.map(c => c.label);
    const colSpan = 2 + comparatives.length * 3;

    const blockOf = (r: SegmentIncomeStatementResponse | null, segmentId: string): SegmentIncomeStatementBlock | undefined =>
        r?.segments.find(b => b.segmentId === segmentId);

    // Union of segments across the current and comparative periods, so the blocks add up to the
    // footer (a segment with activity only in a comparative period shows a zero current column).
    const segmentBlocks = (): SegmentIncomeStatementBlock[] => {
        const out = [...(report?.segments ?? [])];
        const seen = new Set(out.map(b => b.segmentId));
        comparatives.forEach(c => (c.report?.segments ?? []).forEach(b => {
            if (!seen.has(b.segmentId)) {
                seen.add(b.segmentId);
                out.push({ segmentId: b.segmentId, segmentCode: b.segmentCode, segmentName: b.segmentName,
                    revenueLines: [], expenseLines: [], totalRevenue: 0, totalExpense: 0, netProfit: 0 });
            }
        }));
        return out;
    };

    const onToggle = (scopedKey: string, expanded: boolean) => setExpand(s => toggleKey(s, scopedKey, expanded));

    const renderHeader = () => (
        <thead>
            <tr>
                <th data-help="accounting.incomeStatement.col.account" style={statementHeaderStyle}>Account</th>
                <FigureHeaders currentLabel="Current" comparativeLabels={comparativeLabels} />
            </tr>
        </thead>
    );

    const totals = (pick: (b: { totalRevenue: number; totalExpense: number; netProfit: number }) => number,
                    current: { totalRevenue: number; totalExpense: number; netProfit: number },
                    others: ({ totalRevenue: number; totalExpense: number; netProfit: number } | undefined)[]) =>
        [pick(current), ...others.map(o => (o ? pick(o) : 0))];

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.incomeStatement.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>Income Statement</h2>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <label data-help="accounting.incomeStatement.from" style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label data-help="accounting.incomeStatement.to" style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <SearchableSelect data-help="accounting.incomeStatement.segment"
                    options={segments}
                    value={selectedSegmentId}
                    onChange={(id) => setSelectedSegmentId(id)}
                    getOptionLabel={(seg) => `${seg.code} — ${seg.name}`}
                    label="Segment"
                    placeholder="All Segments"
                    sx={{ flex: '1 1 200px', maxWidth: 300 }}
                />
                <TextField data-help="accounting.incomeStatement.compare"
                    select
                    size="small"
                    label="Compare with"
                    value={compare}
                    onChange={e => setCompare(e.target.value as CompareMode)}
                    SelectProps={{ native: true }}
                    inputProps={{ 'data-testid': 'compare-select' }}
                    sx={{ flex: '1 1 200px', maxWidth: 260 }}
                    InputLabelProps={{ shrink: true }}
                >
                    <option value="NONE">No comparison</option>
                    <option value="PREVIOUS">Previous period</option>
                    <option value="LAST_YEAR">Same period last year</option>
                    <option value="BOTH">Both</option>
                </TextField>
                <Button data-help="accounting.incomeStatement.apply" variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Button data-help="accounting.incomeStatement.exportCsv"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || !hasData}
                >
                    Export CSV
                </Button>
                <Button data-help="accounting.incomeStatement.exportPdf" variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownload('pdf')} disabled={loading || downloading || !hasData}>
                    PDF
                </Button>
                <Button data-help="accounting.incomeStatement.exportXlsx" variant="outlined" startIcon={<XlsxIcon />} onClick={() => handleDownload('xlsx')} disabled={loading || downloading || !hasData}>
                    Excel
                </Button>
                <ExpandControls onExpandAll={() => setExpand(expandAll())} onCollapseAll={() => setExpand(collapseAll())} />
            </div>

            {applied && comparatives.length > 0 && (
                <div style={{ color: '#666', fontSize: '0.85rem', marginBottom: '12px' }}>
                    Current: {formatDate(applied.from)} to {formatDate(applied.to)}
                    {applied.previous && <> · Previous period: {formatDate(applied.previous.from)} to {formatDate(applied.previous.to)}</>}
                    {applied.lastYear && <> · Same period last year: {formatDate(applied.lastYear.from)} to {formatDate(applied.lastYear.to)}</>}
                </div>
            )}

            {/* Loading state */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            )}

            {/* Empty state */}
            {!loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No posted entries in the selected period.
                </div>
            )}

            {/* Segment blocks */}
            {!loading && hasData && segmentBlocks().map((block: SegmentIncomeStatementBlock) => {
                const others = comparatives.map(c => blockOf(c.report, block.segmentId));
                const periodLines = (pick: (b: SegmentIncomeStatementBlock) => SegmentIncomeStatementBlock['revenueLines']) =>
                    [pick(block), ...others.map(o => (o ? pick(o) : []))];
                const scopeBase = block.segmentId ?? 'unassigned';
                const ledgerHref = (accountId: string) => ledgerLink(accountId, applied!.from, applied!.to, applied!.segmentId);
                return (
                    <div key={block.segmentId ?? 'unassigned'} style={{ marginBottom: '32px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {block.segmentCode ? `${block.segmentCode} — ` : ''}{block.segmentName}
                            </span>
                        </div>

                        {/*
                          * One scroll box and ONE table for the whole block: Revenue, Expenses and Net
                          * Profit share a single column grid under one header, so a total can never
                          * scroll out from under the heading it is read against.
                          */}
                        <ScrollableTable>
                            <table data-help-section="accounting.incomeStatement.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 + comparatives.length * 330 }}>
                                {renderHeader()}
                                <tbody>
                                    <SectionLabelRow label="Revenue" colSpan={colSpan} />
                                    <StatementTreeRows
                                        scope={`${scopeBase}:rev`}
                                        perPeriod={periodLines(b => b.revenueLines)}
                                        accounts={accounts}
                                        state={expand}
                                        onToggle={onToggle}
                                        ledgerHref={ledgerHref}
                                    />
                                    <TotalRow label="Total Revenue" background="#e8f5e9" color="#2e7d32" borderTop="2px solid #a5d6a7"
                                        values={totals(b => b.totalRevenue, block, others)} />
                                    <SectionLabelRow label="Expenses" colSpan={colSpan} />
                                    <StatementTreeRows
                                        scope={`${scopeBase}:exp`}
                                        perPeriod={periodLines(b => b.expenseLines)}
                                        accounts={accounts}
                                        state={expand}
                                        onToggle={onToggle}
                                        ledgerHref={ledgerHref}
                                    />
                                    <TotalRow label="Total Expense" background="#fce4ec" color="#c62828" borderTop="2px solid #f48fb1"
                                        values={totals(b => b.totalExpense, block, others)} />
                                    <TotalRow label="Net Profit" background="#f3f4f6" color={netProfitColor(block.netProfit)} borderTop="2px solid #9e9e9e"
                                        values={totals(b => b.netProfit, block, others)} />
                                </tbody>
                            </table>
                        </ScrollableTable>
                    </div>
                );
            })}

            {/* Grand-total footer */}
            {!loading && hasData && (
                <div style={{ marginTop: '8px' }}>
                    <ScrollableTable>
                        <table data-help-section="accounting.incomeStatement.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 + comparatives.length * 330 }}>
                            {renderHeader()}
                            <tbody>
                                <TotalRow label="Total Revenue" background="#e8f5e9" color="#2e7d32" borderTop="3px solid #66bb6a"
                                    values={totals(b => b.totalRevenue, report!, comparatives.map(c => c.report ?? undefined))} />
                                <TotalRow label="Total Expense" background="#fce4ec" color="#c62828"
                                    values={totals(b => b.totalExpense, report!, comparatives.map(c => c.report ?? undefined))} />
                                <TotalRow label="Net Profit" background="#e8eaf6" color={netProfitColor(report!.netProfit)} borderTop="2px solid #7986cb"
                                    values={totals(b => b.netProfit, report!, comparatives.map(c => c.report ?? undefined))} />
                            </tbody>
                        </table>
                    </ScrollableTable>
                </div>
            )}
        </div>
    );
};

export default SegmentIncomeStatement;
